import React, { useEffect, useState, useCallback } from 'react';
import { getProjectEntries } from '../services/ProjectServices';
import { getTicketsByProject } from '../services/ProjectTicketService';
import { getAllocationsByTicket, moveAllocations } from '../services/ProjectAllocationService';
import { useToast } from '../context/UIContext';
import '../styles/MoveAllocations.css';

function fmt(min) {
    if (!min && min !== 0) return '—';
    const h = Math.floor(Math.abs(min) / 60);
    const m = Math.abs(min) % 60;
    return `${h}h ${String(m).padStart(2, '0')}min`;
}

export default function MoveAllocations() {
    const toast = useToast();
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [tickets, setTickets] = useState([]);
    const [sourceId, setSourceId] = useState('');
    const [targetId, setTargetId] = useState('');
    const [allocs, setAllocs] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [moving, setMoving] = useState(false);

    useEffect(() => { getProjectEntries().then(setProjects).catch(() => {}); }, []);

    const loadAllocs = useCallback((ticketId) => {
        if (!ticketId) { setAllocs([]); return; }
        setLoading(true);
        getAllocationsByTicket(ticketId)
            .then(d => setAllocs(d.allocations || []))
            .catch(() => setAllocs([]))
            .finally(() => setLoading(false));
    }, []);

    const handleProject = (pid) => {
        setProjectId(pid);
        setSourceId('');
        setTargetId('');
        setAllocs([]);
        setSelected(new Set());
        if (pid) {
            getTicketsByProject(pid).then(setTickets).catch(() => setTickets([]));
        } else {
            setTickets([]);
        }
    };

    const handleSource = (tid) => {
        setSourceId(tid);
        setSelected(new Set());
        if (targetId === tid) setTargetId('');
        loadAllocs(tid);
    };

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const allSelected = allocs.length > 0 && selected.size === allocs.length;
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allocs.map(a => a.id)));

    const selectedMinutes = allocs
        .filter(a => selected.has(a.id))
        .reduce((s, a) => s + (a.allocated_minutes || 0), 0);

    const handleMove = async () => {
        if (!targetId || selected.size === 0) return;
        setMoving(true);
        try {
            const res = await moveAllocations({ allocation_ids: [...selected], ticket_id: targetId });
            toast(`${res.moved} Buchung(en) verschoben.`, 'success');
            setSelected(new Set());
            loadAllocs(sourceId);
        } catch {
            toast('Verschieben fehlgeschlagen.', 'error');
        } finally {
            setMoving(false);
        }
    };

    const targetOptions = tickets.filter(t => t.id !== sourceId);
    const targetTitle = tickets.find(t => t.id === targetId)?.title;

    return (
        <div className="ma-page">
            <h2 className="ma-title">Buchungen zwischen Tickets verschieben</h2>
            <p className="ma-sub">
                Wähle ein Projekt, ein Quell- und ein Zielticket, markiere die zu verschiebenden
                Buchungen und klicke „Verschieben".
            </p>

            <div className="ma-controls">
                <label className="ma-field">
                    <span>Projekt</span>
                    <select value={projectId} onChange={e => handleProject(e.target.value)}>
                        <option value="">– wählen –</option>
                        {projects.map(p => (
                            <option key={p.id} value={p.id}>
                                {p.companyName ? `${p.companyName} · ${p.name}` : p.name}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="ma-field">
                    <span>Von Ticket</span>
                    <select value={sourceId} onChange={e => handleSource(e.target.value)} disabled={!projectId}>
                        <option value="">– wählen –</option>
                        {tickets.map(t => (
                            <option key={t.id} value={t.id}>{t.title}{t.is_done ? ' (erledigt)' : ''}</option>
                        ))}
                    </select>
                </label>
                <label className="ma-field">
                    <span>Nach Ticket</span>
                    <select value={targetId} onChange={e => setTargetId(e.target.value)} disabled={!sourceId}>
                        <option value="">– wählen –</option>
                        {targetOptions.map(t => (
                            <option key={t.id} value={t.id}>{t.title}{t.is_done ? ' (erledigt)' : ''}</option>
                        ))}
                    </select>
                </label>
            </div>

            {sourceId && (
                <div className="ma-alloc">
                    <div className="ma-alloc-head">
                        <label className="ma-checkall">
                            <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={allocs.length === 0} />
                            Alle
                        </label>
                        <span className="ma-selinfo">
                            {selected.size} ausgewählt · {fmt(selectedMinutes)}
                        </span>
                        <button
                            className="button"
                            onClick={handleMove}
                            disabled={!targetId || selected.size === 0 || moving}
                            title={!targetId ? 'Zielticket wählen' : ''}
                        >
                            {moving ? 'Verschiebe…' : (targetTitle ? `→ nach „${targetTitle}"` : '→ Verschieben')}
                        </button>
                    </div>

                    {loading ? (
                        <p className="ma-empty">Lade Buchungen…</p>
                    ) : allocs.length === 0 ? (
                        <p className="ma-empty">Keine Buchungen auf diesem Ticket.</p>
                    ) : (
                        <table className="ma-table">
                            <thead>
                                <tr>
                                    <th className="ma-th-check"></th>
                                    <th>Datum</th>
                                    <th>Mitarbeiter</th>
                                    <th>Zeit</th>
                                    <th>Aktivität</th>
                                    <th>Kommentar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocs.map(a => (
                                    <tr
                                        key={a.id}
                                        className={selected.has(a.id) ? 'ma-row--sel' : ''}
                                        onClick={() => toggle(a.id)}
                                    >
                                        <td className="ma-th-check">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(a.id)}
                                                onChange={() => toggle(a.id)}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </td>
                                        <td>{a.date}</td>
                                        <td>{a.user_name || '—'}</td>
                                        <td>{fmt(a.allocated_minutes)}</td>
                                        <td>{a.activity || '—'}</td>
                                        <td className="ma-comment">{a.comment || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}
